import { getTimeZones } from "@vvo/tzdb";
import countriesLib from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
countriesLib.registerLocale(en);

export type TimezoneOption = { label: string; value: string };

function zonesToOptions(zs: ReturnType<typeof getTimeZones>): TimezoneOption[] {
  const filtered = zs.filter(
    (z) =>
      !z.name.startsWith("Etc/") &&
      !z.name.startsWith("GMT") &&
      z.name !== "Factory"
  );

  filtered.sort((a, b) => {
    const diff = a.currentTimeOffsetInMinutes - b.currentTimeOffsetInMinutes;
    if (diff === 0) return a.name.localeCompare(b.name);
    return diff;
  });

  return filtered.map((z) => {
    const mins = z.currentTimeOffsetInMinutes;
    const sign = mins >= 0 ? "+" : "-";
    const abs = Math.abs(mins);
    const hh = String(Math.floor(abs / 60)).padStart(2, "0");
    const mm = String(abs % 60).padStart(2, "0");
    const gmt = `GMT${sign}${hh}:${mm}`;
    const alt = z.alternativeName ? ` — ${z.alternativeName}` : "";
    return { value: z.name, label: `(${gmt}) ${z.name}${alt}` };
  });
}

export function getTimezoneOptionsAll(): TimezoneOption[] {
  return zonesToOptions(getTimeZones());
}

export function getTimezoneOptionsForCountry(
  iso2?: string | null
): TimezoneOption[] {
  if (!iso2) return getTimezoneOptionsAll();
  const upper = iso2.toUpperCase();
  const zs = getTimeZones().filter((z) => z.countryCode === upper);
  const opts = zonesToOptions(zs);
  return opts.length ? opts : getTimezoneOptionsAll();
}

export function getLocalTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function iso2FromCountryName(name?: string | null): string | null {
  if (!name) return null;
  return countriesLib.getAlpha2Code(name, "en") || null;
}

export function suggestZoneByState(
  countryIso2: string | null,
  stateName?: string | null
): string | null {
  if (!countryIso2 || !stateName) return null;
  const c = countryIso2.toUpperCase();

  if (c === "US") {
    const s = stateName.toLowerCase();
    if (
      ["california", "oregon", "washington", "nevada"].some((x) =>
        s.includes(x)
      )
    )
      return "America/Los_Angeles";
    if (
      ["new york", "massachusetts", "florida", "georgia"].some((x) =>
        s.includes(x)
      )
    )
      return "America/New_York";
    if (
      ["texas", "illinois", "alabama", "wisconsin"].some((x) => s.includes(x))
    )
      return "America/Chicago";
    if (["arizona"].some((x) => s.includes(x))) return "America/Phoenix";
    if (["colorado", "utah"].some((x) => s.includes(x)))
      return "America/Denver";
  }

  return null;
}
